/**
 * OmegaLoopPage — `/omega/loop`  (Sprint 49, The Perfect Loop — Wave E).
 *
 * The Founder control screen for the Loop: full visibility (not just budget).
 * Three panes — LOOP ACTIVITY, BUDGET, INSIGHTS — over the durable, signed Loop
 * engine. Founder-only (the route is founder-gated), English-only, responsive
 * 375px+. Reads /admin/loop/summary + /admin/budget/summary + the T3 approval
 * queue; the Founder signs or rejects parked T3 actions in place.
 */

import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { signHashWithFounderKey } from '@/api/real/founderSign';

// ── Response shapes (defensive — fields optional at runtime) ──────────────────

interface LoopRun {
  run_id: string;
  task: string;
  state: string;
  iteration?: number;
  max_iter?: number;
  quality?: number;
  tier?: string;
  cost_usd_cents?: number;
}
interface LoopSummary {
  active_loops?: LoopRun[];
  active_count?: number;
  t3_approval_queue?: LoopRun[];
  degraded?: LoopRun[];
  recent_runs?: LoopRun[];
  total_runs?: number;
}
interface Approval {
  approval_id: string;
  run_id: string;
  action_json?: string;
  blast_radius?: string;
  status?: string;
}
interface ApprovalsResponse {
  approvals?: Approval[];
}
interface SpendKey {
  key: string;
  total_usd_cents: number;
}
interface BudgetSummary {
  spent_month_usd_cents?: number;
  ceiling_month_usd_cents?: number;
  remaining_usd_cents?: number;
  degradation?: string;
  by_supplier?: SpendKey[];
  by_department?: SpendKey[];
  cost_per_successful_task_usd_cents?: number | null;
}

// ── Formatters ────────────────────────────────────────────────────────────────

function usd(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function pct(spent?: number, ceiling?: number): number {
  if (!spent || !ceiling || ceiling <= 0) return 0;
  return Math.min(100, Math.round((spent / ceiling) * 100));
}

const degradeColor: Record<string, string> = {
  normal: '#65a30d',
  degraded: '#d97706',
  hard_stop: '#dc2626',
};

// ── Card primitives ───────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  border: '1px solid var(--omega-border, #2a2a30)',
  borderRadius: 8,
  padding: '12px 14px',
  background: 'var(--omega-canvas, #15151a)',
};
const sectionTitle: React.CSSProperties = {
  fontSize: '10pt',
  fontWeight: 700,
  margin: '20px 0 10px',
};
const muted: React.CSSProperties = { fontSize: '8.5pt', opacity: 0.65 };

export function OmegaLoopPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const qc = useQueryClient();

  const summary = useQuery({
    queryKey: ['loop', 'summary'],
    queryFn: () => apiFetch<LoopSummary>('/admin/loop/summary'),
    refetchInterval: 5_000,
  });
  const approvals = useQuery({
    queryKey: ['loop', 'approvals'],
    queryFn: () => apiFetch<ApprovalsResponse>('/admin/loop/approvals'),
    refetchInterval: 5_000,
  });
  const budget = useQuery({
    queryKey: ['loop', 'budget'],
    queryFn: () => apiFetch<BudgetSummary>('/admin/budget/summary'),
    refetchInterval: 15_000,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['loop'] });
  };

  const sign = async (approvalId: string) => {
    const founder_signature = await signHashWithFounderKey(approvalId);
    await apiFetch(`/admin/loop/approvals/${approvalId}/sign`, {
      method: 'POST',
      body: { founder_signature },
    });
    refresh();
  };
  const reject = async (approvalId: string) => {
    await apiFetch(`/admin/loop/approvals/${approvalId}/reject`, { method: 'POST' });
    refresh();
  };

  const active = summary.data?.active_loops ?? [];
  const recent = summary.data?.recent_runs ?? [];
  const queue = approvals.data?.approvals ?? [];
  const degraded = summary.data?.degraded ?? [];
  const b = budget.data;

  return (
    <div className="omega-page" style={{ padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'The Loop' }]} />
      <h1 style={{ fontSize: '12pt', fontWeight: 700, margin: '12px 0 4px' }}>The Loop</h1>
      <p style={muted}>
        What OMEGA is doing right now, what it costs, and what needs your sign-off.
      </p>

      {/* ── LOOP ACTIVITY ── */}
      <h2 style={sectionTitle}>Activity</h2>
      {active.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {active.map((r) => (
            <div key={r.run_id} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: '9.5pt', fontWeight: 600 }}>{r.task}</span>
                <span style={muted}>
                  {r.tier ?? 'T1'} · iter {r.iteration ?? 0}/{r.max_iter ?? 20}
                </span>
              </div>
              <div style={{ ...muted, marginTop: 3 }}>
                state: {r.state} · quality {Math.round((r.quality ?? 0) * 100)}% · spent{' '}
                {usd(r.cost_usd_cents)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={muted}>No active loops right now.</p>
      )}

      {/* ── RECENT ACTIVITY (incl. completed/mirrored chain actions) ── */}
      <h2 style={sectionTitle}>Recent activity ({recent.length})</h2>
      {recent.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {recent.map((r) => (
            <div key={`recent-${r.run_id}`} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: '9.5pt', fontWeight: 600 }}>{r.task}</span>
                <span style={muted}>{r.tier ?? 'T1'}</span>
              </div>
              <div style={{ ...muted, marginTop: 3 }}>
                state: {r.state} · spent {usd(r.cost_usd_cents)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={muted}>No chain activity yet — run an agent on the Chain screen.</p>
      )}

      {/* ── T3 APPROVAL QUEUE ── */}
      <h2 style={sectionTitle}>Approvals needed ({queue.length})</h2>
      {queue.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {queue.map((a) => (
            <div key={a.approval_id} style={card}>
              <div style={{ fontSize: '9pt', marginBottom: 6 }}>
                <span style={{ fontWeight: 600 }}>T3 action</span>
                {a.blast_radius ? <span style={muted}> · blast {a.blast_radius}</span> : null}
                <div style={{ ...muted, marginTop: 3, wordBreak: 'break-word' }}>
                  {a.action_json ?? '(action)'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => void sign(a.approval_id)}
                  style={{ fontSize: '8.5pt', padding: '4px 10px' }}
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => void reject(a.approval_id)}
                  style={{ fontSize: '8.5pt', padding: '4px 10px' }}
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={muted}>Nothing waiting on your approval.</p>
      )}

      {degraded.length > 0 && (
        <>
          <h2 style={sectionTitle}>Degraded / stuck ({degraded.length})</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {degraded.map((r) => (
              <div key={r.run_id} style={card}>
                <span style={{ fontSize: '9pt' }}>{r.task}</span>{' '}
                <span style={muted}>— {r.state}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── BUDGET ── */}
      <h2 style={sectionTitle}>Budget</h2>
      {b ? (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: '11pt', fontWeight: 700 }}>
              {usd(b.spent_month_usd_cents)}{' '}
              <span style={muted}>/ {usd(b.ceiling_month_usd_cents)} this month</span>
            </span>
            <span
              style={{
                fontSize: '8pt',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: degradeColor[b.degradation ?? 'normal'] ?? '#9ca3af',
              }}
            >
              {b.degradation ?? 'normal'}
            </span>
          </div>
          <div
            style={{
              height: 6,
              borderRadius: 3,
              background: 'rgba(255,255,255,0.08)',
              margin: '8px 0',
            }}
          >
            <div
              style={{
                width: `${pct(b.spent_month_usd_cents, b.ceiling_month_usd_cents)}%`,
                height: '100%',
                borderRadius: 3,
                background: degradeColor[b.degradation ?? 'normal'] ?? '#65a30d',
              }}
            />
          </div>
          <div style={muted}>
            Remaining {usd(b.remaining_usd_cents)} · cost / successful task{' '}
            {usd(b.cost_per_successful_task_usd_cents)}
          </div>
          {(b.by_supplier?.length ?? 0) > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ ...muted, marginBottom: 4 }}>By supplier</div>
              {b.by_supplier!.slice(0, 8).map((s) => (
                <div
                  key={s.key}
                  style={{ display: 'flex', justifyContent: 'space-between', fontSize: '8.5pt' }}
                >
                  <span>{s.key || '(unattributed)'}</span>
                  <span>{usd(s.total_usd_cents)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p style={muted}>No budget data yet.</p>
      )}

      {/* ── INSIGHTS (placeholder until the recommender lands) ── */}
      <h2 style={sectionTitle}>Insights</h2>
      <p style={muted}>
        {b?.degradation === 'degraded'
          ? 'Spending is over 80% of the monthly ceiling — OMEGA is routing research to Haiku to slow the burn.'
          : 'No cost anomalies detected. Efficiency recommendations appear here as the Loop accrues history.'}
      </p>
    </div>
  );
}
